import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { SocialButtons } from "@/components/Socials";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", role: "Parent", subject: "", message: "" };

const inputCls =
  "w-full px-4 py-2.5 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C2A]/40 focus:border-[#0F4C2A]";

export default function Contact() {
  const { site } = useSite();
  const c = site.content;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const hasSocials = Boolean(c.social_twitter || c.social_instagram || c.social_facebook);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(`Message sent! ${c.contact_coach} will get back to you soon.`);
      setForm(EMPTY);
    } catch {
      toast.error(`Something went wrong. Please email ${c.contact_email} directly.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Get In Touch"
        title="Contact The Program"
        subtitle={c.subtitle_contact}
        testid="contact-header"
      />

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5" htmlFor="cf-name">Full Name *</label>
                  <input id="cf-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-form-name" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5" htmlFor="cf-email">Email *</label>
                  <input id="cf-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-form-email" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5" htmlFor="cf-phone">Phone</label>
                  <input id="cf-phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-form-phone" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5" htmlFor="cf-role">I am a... *</label>
                  <select id="cf-role" value={form.role} onChange={set("role")} className={inputCls} data-testid="contact-form-role">
                    <option>Parent</option>
                    <option>Prospective Player</option>
                    <option>Current Player</option>
                    <option>Alumni</option>
                    <option>Media</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5" htmlFor="cf-subject">Subject *</label>
                <input id="cf-subject" required value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-form-subject" />
              </div>
              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5" htmlFor="cf-message">Message *</label>
                <textarea id="cf-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-form-message" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit"
                className="mt-6 inline-flex items-center gap-2 bg-[#0F4C2A] hover:bg-[#0A3823] disabled:opacity-60 text-white px-7 py-3 rounded-md font-bold text-sm uppercase tracking-wide transition-colors shadow-md"
              >
                <Send size={15} /> {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="bg-[#0F4C2A] diagonal-stripes text-white rounded-lg p-7 sm:p-8 shadow-sm h-full" data-testid="contact-info-panel">
              <h2 className="font-condensed text-2xl font-extrabold uppercase tracking-tight">Direct Contact</h2>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">The fastest way to reach the program is directly through {c.contact_coach}.</p>
              <ul className="mt-7 space-y-5">
                <li className="flex items-start gap-3.5">
                  <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center shrink-0"><Mail size={17} className="text-[#F59E0B]" /></div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Email</p>
                    <a href={`mailto:${c.contact_email}`} className="text-sm font-semibold hover:text-[#F59E0B] transition-colors" data-testid="contact-info-email">{c.contact_email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3.5">
                  <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center shrink-0"><Phone size={17} className="text-[#F59E0B]" /></div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Phone</p>
                    <a href={`tel:${c.contact_phone}`} className="text-sm font-semibold hover:text-[#F59E0B] transition-colors" data-testid="contact-info-phone">{c.contact_phone}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3.5">
                  <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center shrink-0"><MapPin size={17} className="text-[#F59E0B]" /></div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">School</p>
                    <p className="text-sm font-semibold">{c.contact_school}</p>
                    <p className="text-sm text-white/70">{c.contact_address}</p>
                  </div>
                </li>
              </ul>
              {hasSocials && (
                <div className="mt-8 pt-6 border-t border-white/15" data-testid="contact-socials">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-3">Follow The Bears</p>
                  <SocialButtons />
                </div>
              )}
              <div className="mt-8 pt-6 border-t border-white/15">
                <p className="text-xs text-white/70 leading-relaxed">For tryout information, summer workouts, and off-season training, mention it in your message and the coaching staff will follow up with details.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
